import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';

import { MDBox, MDTypography, MDButton } from '@components/index';
import MainLayout from '@layouts/MainLayout';
import UserAuth from '@services/auth';
import RoutesPath from '@constants/RoutesPath';
import { useRedeemMicroClaimMutation } from '@api/microClaim';

type ClaimState = 'loading' | 'awaiting_auth' | 'success' | 'invalid' | 'error';

function MicroClaim(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const claimId = searchParams.get('claimId') ?? '';
  const claimAssetId = searchParams.get('claimAssetId') ?? '';

  const [claimState, setClaimState] = useState<ClaimState>('loading');
  const [planEndDate, setPlanEndDate] = useState('');

  const [redeemClaim] = useRedeemMicroClaimMutation();

  const claimUrl = `/claim?claimId=${claimId}&claimAssetId=${claimAssetId}`;

  useEffect(() => {
    // Missing params — nothing we can do
    if (!claimId || !claimAssetId) {
      setClaimState('invalid');

      return;
    }

    // Not logged in — ask buyer to authenticate first
    if (!UserAuth.isAuthenticated) {
      setClaimState('awaiting_auth');

      return;
    }

    // Logged in — attempt redemption immediately
    setClaimState('loading');
    redeemClaim({ claimId, claimAssetId })
      .unwrap()
      .then((data) => {
        setPlanEndDate(data.plan_end_date);
        setClaimState('success');
      })
      .catch((err) => {
        const status = err?.status;

        if (status === 400) setClaimState('invalid');
        else setClaimState('error');
      });
  }, [claimId, claimAssetId, UserAuth.isAuthenticated]);

  const renderContent = () => {
    switch (claimState) {
      case 'loading':
        return (
          <MDBox display='flex' flexDirection='column' alignItems='center' py={4} gap={2}>
            <CircularProgress color='primary' />
            <MDTypography variant='body2' color='text'>
              Activating your plan…
            </MDTypography>
          </MDBox>
        );

      case 'awaiting_auth':
        return (
          <MDBox py={2}>
            <MDTypography variant='body2' color='text' mb={3}>
              Please sign in or create an account to activate your purchase. Your plan will be
              applied immediately after you log in.
            </MDTypography>
            <MDBox display='flex' flexDirection='column' gap={1.5}>
              <MDButton
                variant='gradient'
                color='primary'
                fullWidth
                component={Link}
                to={`${RoutesPath.SIGN_IN}?next=${encodeURIComponent(claimUrl)}`}
              >
                Sign in
              </MDButton>
              <MDButton
                variant='outlined'
                color='primary'
                fullWidth
                component={Link}
                to={`${RoutesPath.SIGN_UP}?next=${encodeURIComponent(claimUrl)}`}
              >
                Create account
              </MDButton>
            </MDBox>
          </MDBox>
        );

      case 'success':
        return (
          <MDBox py={2} textAlign='center'>
            <MDTypography variant='h5' color='success' mb={1}>
              Your plan is now active!
            </MDTypography>
            <MDTypography variant='body2' color='text' mb={3}>
              Your purchase has been confirmed. Your plan is valid until{' '}
              <strong>{planEndDate}</strong>. You can now upload your file.
            </MDTypography>
            <MDButton
              variant='gradient'
              color='primary'
              fullWidth
              onClick={() => navigate(RoutesPath.MY_PROJECTS)}
            >
              Go to my projects
            </MDButton>
          </MDBox>
        );

      case 'invalid':
        return (
          <MDBox py={2} textAlign='center'>
            <MDTypography variant='h5' color='error' mb={1}>
              Invalid claim link
            </MDTypography>
            <MDTypography variant='body2' color='text'>
              This claim link is invalid or has already been used. If you believe this is an
              error, please contact support.
            </MDTypography>
          </MDBox>
        );

      case 'error':
      default:
        return (
          <MDBox py={2} textAlign='center'>
            <MDTypography variant='h5' color='error' mb={1}>
              Something went wrong
            </MDTypography>
            <MDTypography variant='body2' color='text' mb={3}>
              We could not activate your plan right now. Please try again or contact support.
            </MDTypography>
            <MDButton
              variant='outlined'
              color='primary'
              onClick={() => window.location.reload()}
            >
              Try again
            </MDButton>
          </MDBox>
        );
    }
  };

  return (
    <MainLayout pageTitle='Activate your purchase' sectionTitle='CLAIM'>
      <MDBox display='flex' justifyContent='center'>
        <Card sx={{ maxWidth: 480, width: '100%' }}>
          <MDBox
            variant='gradient'
            bgColor='primary'
            borderRadius='lg'
            coloredShadow='primary'
            mx={2}
            mt={3}
            p={3}
            mb={1}
            textAlign='center'
          >
            <MDTypography variant='h4' fontWeight='medium' color='white' mt={1}>
              Activate your purchase
            </MDTypography>
            <MDTypography display='block' variant='button' color='white' my={1}>
              DataSquirrel — purchased via Micro
            </MDTypography>
          </MDBox>
          <MDBox pt={3} pb={3} px={3}>
            {renderContent()}
          </MDBox>
        </Card>
      </MDBox>
    </MainLayout>
  );
}

export default MicroClaim;
